/**
 * Inactivity watchdog for analyst sessions.
 *
 * Logs the analyst out after the tenant's analystTimeOut so their wires go back
 * to the pool. The logout runs through the normal AUTH_LOGOUT chain, so nothing
 * here needs to know about round-robin.
 *
 * Kept out of App.vue so it can be tested directly — the Vue SFC transform is
 * unusable in this repo's jest setup.
 */

// Pointer movement alone misses keyboard-driven review, scrolling a hit
// list, and touch devices.
export const ACTIVITY_EVENTS = [
	"mousemove",
	"mousedown",
	"keydown",
	"wheel",
	"scroll",
	"touchstart",
];

// The tenant field offers 15/30/60 and defaults to 15 at creation.
export const DEFAULT_ANALYST_TIMEOUT_MINUTES = 15;

export const ACTIVITY_DEBOUNCE_MS = 500;

// capture: `scroll` does not bubble — see start().
export const ACTIVITY_LISTENER_OPTIONS = { capture: true, passive: true };

/**
 * Resolve the configured inactivity window.
 *
 * A missing or non-positive value means the profile did not load, not that
 * someone chose it. The `> 0` guard mirrors jobs-runner's
 * `options.analystTimeOut: { $gt: 0 }` filter.
 *
 * @param {unknown} configured
 * @param {(msg: string) => void} [warn]
 * @returns {number} minutes
 */
export const resolveTimeoutMinutes = (configured, warn = console.warn) => {
	const minutes = Number(configured);
	if (Number.isFinite(minutes) && minutes > 0) return minutes;
	warn(
		"analystTimeOut missing from tenant options — falling back to " +
			`${DEFAULT_ANALYST_TIMEOUT_MINUTES} minutes`,
	);
	return DEFAULT_ANALYST_TIMEOUT_MINUTES;
};

/**
 * Create an inactivity watchdog.
 *
 * @param {object} options
 * @param {() => unknown} options.getTimeoutMinutes  tenant analystTimeOut
 * @param {() => boolean} options.isEnabled          arm only while this is true
 * @param {(minutes: number) => void} options.onTimeout
 * @param {Document} [options.doc]
 * @param {(msg: string) => void} [options.warn]
 */
export const createIdleWatchdog = ({
	getTimeoutMinutes,
	isEnabled,
	onTimeout,
	doc = document,
	warn = console.warn,
}) => {
	let timer = null;
	let debounce = null;
	// arm() runs on every activity burst, so warn once rather than per re-arm.
	let warnedMissing = false;
	const warnOnce = (msg) => {
		if (warnedMissing) return;
		warnedMissing = true;
		warn(msg);
	};

	const clear = () => {
		if (timer) {
			clearTimeout(timer);
			timer = null;
		}
	};

	const arm = () => {
		clear();
		if (!isEnabled()) return;
		const minutes = resolveTimeoutMinutes(getTimeoutMinutes(), warnOnce);
		timer = setTimeout(
			() => {
				timer = null;
				onTimeout(minutes);
			},
			minutes * 60 * 1000,
		);
	};

	const onActivity = () => {
		if (!isEnabled()) return;
		if (debounce) clearTimeout(debounce);
		debounce = setTimeout(() => {
			debounce = null;
			arm();
		}, ACTIVITY_DEBOUNCE_MS);
	};

	// Returning to a backgrounded tab is activity; leaving it is not.
	const onVisibilityChange = () => {
		if (!doc.hidden) onActivity();
	};

	const start = () => {
		for (const evt of ACTIVITY_EVENTS) {
			// Capture phase: `scroll` does not bubble, so a document listener never
			// sees an inner container (querylog tables, hit lists).
			doc.addEventListener(evt, onActivity, ACTIVITY_LISTENER_OPTIONS);
		}
		doc.addEventListener("visibilitychange", onVisibilityChange);
		arm();
	};

	const stop = () => {
		for (const evt of ACTIVITY_EVENTS) {
			// The flag must match the one used to add, or nothing is removed.
			doc.removeEventListener(evt, onActivity, ACTIVITY_LISTENER_OPTIONS);
		}
		doc.removeEventListener("visibilitychange", onVisibilityChange);
		if (debounce) {
			clearTimeout(debounce);
			debounce = null;
		}
		clear();
	};

	return { start, stop, refresh: arm, isArmed: () => timer !== null };
};
